import Big from 'big.js';

type FormatPercentageOptions = {
  /**
   * Number of decimal places
   * @default 2
   */
  decimals?: number;

  /**
   * Whether to show trailing zeros
   * @default true
   */
  showTrailingZeros?: boolean;

  /**
   * Rounding method
   * @default 'banker' - Round half to even (Big.roundHalfEven)
   * 'standard' - Round half up (Big.roundHalfUp)
   */
  rounding?: 'banker' | 'standard';
};

/**
 * Formats a number as a percentage string.
 *
 * @param value - The number to format (e.g., 3.5 for 3.50%). Must be a valid number.
 * @param options - Optional formatting options
 * @returns Formatted percentage string
 *
 * @remarks
 * - Default: 2 decimal places with trailing zeros (e.g., "3.50%")
 * - Uses banker's rounding by default (round half to even)
 * - Returns "—" for NaN or Infinity
 * - Strips negative sign ONLY from values that round to exactly zero (e.g., -0.001 → "0.00%")
 * - Non-zero negatives keep their sign (e.g., -0.5 → "-0.50%", -0.01 → "-0.01%")
 * - **Caller must handle null/undefined values** - use conditional rendering or fallback
 *
 * @example
 * ```ts
 * // Basic usage
 * formatPercentage(3.5)  // "3.50%"
 * formatPercentage(100)  // "100.00%"
 *
 * // With options
 * formatPercentage(3.5, { showTrailingZeros: false })    // "3.5%"
 * formatPercentage(100, { showTrailingZeros: false })    // "100%"
 * formatPercentage(3.445, { rounding: 'standard' })      // "3.45%"
 * formatPercentage(3.125, { decimals: 3 })               // "3.125%"
 *
 * // Edge cases - Negative sign handling
 * formatPercentage(-0.5)      // "-0.50%" (keeps negative - value is not zero)
 * formatPercentage(-0.01)     // "-0.01%" (keeps negative - value is not zero)
 * formatPercentage(-0.001)    // "0.00%" (strips negative - rounds to zero)
 * formatPercentage(-0.0001)   // "0.00%" (strips negative - rounds to zero)
 *
 * // Edge cases - Invalid values
 * formatPercentage(NaN)       // "—"
 * formatPercentage(Infinity)  // "—"
 *
 * // Handling nullable values (caller's responsibility)
 * {rate != null && formatPercentage(rate)}
 * formatPercentage(rate ?? 0)
 * ```
 */
export const formatPercentage = (value: number, options: FormatPercentageOptions = {}): string => {
  const { decimals = 2, showTrailingZeros = true, rounding = 'banker' } = options;

  // Handle invalid numeric values (NaN, Infinity)
  if (!Number.isFinite(value)) {
    return '—';
  }

  const roundingMode = rounding === 'standard' ? Big.roundHalfUp : Big.roundHalfEven;

  let formatted = Big(value).toFixed(decimals, roundingMode);

  // Strip negative sign from values that round to exactly zero (e.g., -0.001 → "0.00%" not "-0.00%")
  // Non-zero negative values like -0.5 keep their negative sign
  if (parseFloat(formatted) === 0 && formatted.startsWith('-')) {
    formatted = formatted.substring(1);
  }

  // Remove trailing zeros if requested
  if (!showTrailingZeros) {
    formatted = parseFloat(formatted).toString();
  }

  return `${formatted}%`;
};
